/**
 * Design Conditions API — configuration-level constants (speeds, weights, etc.).
 */

import { apiClient } from "./client";

export interface DesignCondition {
  id: string;
  configuration_id: string;
  project_id: string;
  parameter: string;
  value: number;
  units: string | null;
  category: string | null;
  description: string | null;
  owned_by: string | null;
  change_reason: string | null;
  approval_status: string;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface DesignConditionUpsert {
  configuration_id: string;
  project_id: string;
  parameter: string;
  value: number;
  units?: string;
  category?: string;
  description?: string;
  owned_by?: string;
  change_reason?: string;
}

export interface DesignConditionImportItem {
  parameter: string;
  value: number;
  units?: string;
  category?: string;
}

export function fetchDesignConditions(
  configurationId: string,
): Promise<DesignCondition[]> {
  const params = new URLSearchParams({ configuration_id: configurationId });
  return apiClient<DesignCondition[]>(`/design-conditions?${params}`);
}

export function upsertDesignCondition(
  body: DesignConditionUpsert,
): Promise<DesignCondition> {
  return apiClient<DesignCondition>("/design-conditions", {
    method: "POST",
    body,
  });
}

export function importDesignConditions(
  configurationId: string,
  projectId: string,
  conditions: DesignConditionImportItem[],
): Promise<{ imported: number; conditions: DesignCondition[] }> {
  return apiClient("/design-conditions/import", {
    method: "POST",
    body: {
      configuration_id: configurationId,
      project_id: projectId,
      conditions,
    },
  });
}

export function deleteDesignCondition(
  conditionId: string,
  reason: string,
): Promise<{ status: string; id: string }> {
  return apiClient(`/design-conditions/${conditionId}`, {
    method: "DELETE",
    body: { reason },
  });
}

export function approveDesignCondition(
  conditionId: string,
): Promise<{ status: string; id: string }> {
  return apiClient(`/design-conditions/${conditionId}/approve`, {
    method: "POST",
    body: {},
  });
}

export function rejectDesignCondition(
  conditionId: string,
  comment?: string,
): Promise<{ status: string; id: string }> {
  return apiClient(`/design-conditions/${conditionId}/reject`, {
    method: "POST",
    body: { comment },
  });
}
